import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  useConnect,
  useConnection,
  useConnectors,
  useDisconnect,
  useReconnect,
  useSwitchChain,
} from 'wagmi'
import type { Connector } from 'wagmi'
import type { Address } from 'viem'
import { CHAIN, CHAIN_ID } from '../config/faucet.config'
import { isUserRejection, readableError } from '../lib/format'
import { forgetConnection, rememberConnection, rememberedConnection } from '../lib/connection'

export interface Wallet {
  address?: Address
  isConnected: boolean
  isConnecting: boolean
  isReconnecting: boolean
  /** Connected, but to something other than Base mainnet. */
  isWrongNetwork: boolean
  chainId?: number
  connectors: readonly Connector[]
  /** Resolves true only when the wallet actually connected. */
  connect: (connector: Connector) => Promise<boolean>
  disconnect: () => void
  switchToBase: () => Promise<void>
  isSwitching: boolean
  error: string | null
  clearError: () => void
}

/**
 * Real wallet connection and network switching.
 *
 * There is no simulated connection state: `isConnected` comes from the wallet,
 * and `isWrongNetwork` compares the wallet's actual chain to Base mainnet
 * (8453). Switching goes through wagmi's `switchChain`, whose injected
 * connector already falls back to `wallet_addEthereumChain` when the wallet
 * does not have Base yet.
 */
export function useWallet(): Wallet {
  const connection = useConnection()
  const connectors = useConnectors()
  const { mutateAsync: connectAsync } = useConnect()
  const { mutate: disconnectMutate } = useDisconnect()
  const { mutate: reconnectMutate } = useReconnect()
  const { mutateAsync: switchChainAsync, isPending: isSwitching } = useSwitchChain()

  const [error, setError] = useState<string | null>(null)

  /**
   * Restore a previous session — but only one the user actually started here.
   *
   * `reconnectOnMount` is off on the provider, so this is the only path back to
   * a connected state on load. It runs at most once, and only when the app's own
   * flag is present, which is what keeps a visitor whose MetaMask has merely
   * authorised this domain from being connected without asking, and what makes
   * an explicit disconnect survive a reload.
   */
  const hasAttemptedReconnect = useRef(false)
  useEffect(() => {
    if (hasAttemptedReconnect.current) return
    hasAttemptedReconnect.current = true

    const rememberedId = rememberedConnection()
    if (!rememberedId) return

    const match = connectors.find((c) => c.id === rememberedId)
    // If that wallet is no longer installed there is nothing to restore; the
    // flag is left alone so the session returns if the user reinstalls it.
    if (!match) return

    reconnectMutate({ connectors: [match] })
  }, [connectors, reconnectMutate])

  const connect = useCallback(
    async (connector: Connector): Promise<boolean> => {
      setError(null)
      try {
        await connectAsync({ connector, chainId: CHAIN_ID })
        rememberConnection(connector.id)
        return true
      } catch (err) {
        if (!isUserRejection(err)) setError(readableError(err))
        return false
      }
    },
    [connectAsync],
  )

  const switchToBase = useCallback(async () => {
    setError(null)
    try {
      await switchChainAsync({ chainId: CHAIN_ID })
    } catch (err) {
      // wagmi's injected connector wraps *every* failure after its 4902
      // add-chain branch in UserRejectedRequestError, so a genuine rejection
      // and a wallet that refused to add the chain look identical here. We
      // always leave a message rather than silently doing nothing.
      setError(
        isUserRejection(err)
          ? `The network switch was not completed. Approve it in your wallet, or add ${CHAIN.name} (chain id ${CHAIN_ID}) manually.`
          : `${readableError(err)} — add ${CHAIN.name} (chain id ${CHAIN_ID}) to your wallet manually if this keeps failing.`,
      )
    }
  }, [switchChainAsync])

  const disconnect = useCallback(() => {
    setError(null)
    // Clear the app's record FIRST. wagmi's own per-connector shim keys are not
    // shared between the targeted and untargeted injected connectors, so this
    // flag — not the shim — is what guarantees the next load stays disconnected.
    forgetConnection()
    disconnectMutate()
  }, [disconnectMutate])

  return useMemo(
    () => ({
      address: connection.address,
      isConnected: connection.isConnected,
      isConnecting: connection.isConnecting,
      isReconnecting: connection.isReconnecting,
      isWrongNetwork: connection.isConnected && connection.chainId !== CHAIN_ID,
      chainId: connection.chainId,
      connectors,
      connect,
      disconnect,
      switchToBase,
      isSwitching,
      error,
      clearError: () => setError(null),
    }),
    [connection, connectors, connect, disconnect, switchToBase, isSwitching, error],
  )
}
